<template>
  <GraphqlDataTable
    :query="query"
    :order-by="defaultOrderBy"
    :filter-black-list="filterBlackList"
    :additional-query-variables="additionalQueryVariables"
    :search-placeholder="$t('Search staffing agencies...')"
    :max-height-adjustment="30"
    :quick-filters="['statuses']"
  >
    <template #action="{ row, index }">
      <RecruiterComplianceActionsColumn :row="row" :index="index" @review="handleReview" />
    </template>
    <template #no-results>
      <NoComplianceFound>
        <template #main>{{ $t('No staffing agencies found') }}</template>
        <template #sub>
          {{
            $t(
              "Once you invite staffing agencies and they submit their information, you'll be able to review their compliance information here.",
            )
          }}
        </template>
        <template #actions>
          <WdsButton primary :href="laravelNamedRoute(namedRoutes.recruiters.index)">
            {{ $t('Manage staffing agencies') }}
          </WdsButton>
        </template>
      </NoComplianceFound>
    </template>
  </GraphqlDataTable>

  <RecruiterComplianceReviewModal
    v-if="selectedCompanyRecruiterId"
    :show="showReviewModal"
    :company-recruiter-id="selectedCompanyRecruiterId"
    @close="closeReviewModal"
  />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import { WdsButton } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { RecruiterOrderByColumn, RecruiterStatus, SortOrder } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import RecruiterComplianceActionsColumn from '@/components/GraphqlDataTable/columns/Compliance/RecruiterComplianceActionsColumn.vue'
import RecruiterComplianceReviewModal from '@/components/compliances/RecruiterComplianceReviewModal.vue'
import NoComplianceFound from '@/components/compliances/NoComplianceFound.vue'

const selectedCompanyRecruiterId = ref<string | null>(null)
const showReviewModal = ref(false)

function handleReview(companyRecruiter: { id: string }) {
  selectedCompanyRecruiterId.value = companyRecruiter.id
  showReviewModal.value = true
}

function closeReviewModal() {
  showReviewModal.value = false
  selectedCompanyRecruiterId.value = null
}

const additionalQueryVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  statuses: [RecruiterStatus.Active, RecruiterStatus.Invited],
}))

const filterBlackList = ['accounts', 'tags', 'externalIdentifiers']

const query = graphql(`
  query ComplianceCompanyRecruitersTable(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [RecruiterStatus!]
    $recruiterRegions: MarketRegionInput
    $orderBy: [RecruiterOrderByClauseInput!]
    $verifiedRecruiterReview: VerifiedRecruiterReviewFilterInput
    $companyRecruiterCustomFields: [CustomFieldTypeValueInput!]
  ) {
    companyRecruiters(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      recruiterRegions: $recruiterRegions
      orderBy: $orderBy
      verifiedRecruiterReview: $verifiedRecruiterReview
      customFields: $companyRecruiterCustomFields
    ) {
      data {
        ...RecruiterComplianceInvitedOnColumn
        ...CompanyRecruiterNameColumn
        ...RecruiterComplianceStatusColumn
        ...RecruiterComplianceUpdatedByColumn
        ...RecruiterComplianceActionsColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const defaultOrderBy = {
  field: RecruiterOrderByColumn.RecruiterInvitedAt,
  order: SortOrder.Desc,
}
</script>
